import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';
export type ErrorSource =
  | 'document-parser'
  | 'email-processor'
  | 'dropbox-api'
  | 'vintrace-api'
  | 'database'
  | 'edge-function'
  | 'frontend';

export interface ErrorLog {
  id: string;
  org_id: string;
  severity: ErrorSeverity;
  source: ErrorSource;
  error_code: string | null;
  message: string;
  stack_trace: string | null;
  entity_type: string | null;
  entity_id: string | null;
  user_id: string | null;
  request_id: string | null;
  context: Record<string, any>;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  occurred_at: string;
  // Joined data
  user?: {
    display_name: string;
    email: string;
  };
  resolver?: {
    display_name: string;
  };
}

interface UseErrorLogsParams {
  severity?: ErrorSeverity;
  source?: ErrorSource;
  entityType?: string;
  entityId?: string;
  resolved?: boolean;
  startDate?: Date;
  endDate?: Date;
  searchQuery?: string;
  limit?: number;
}

// ============================================================================
// Hook: useErrorLogs
// ============================================================================

export function useErrorLogs(params: UseErrorLogsParams = {}) {
  const {
    severity,
    source,
    entityType,
    entityId,
    resolved,
    startDate,
    endDate,
    searchQuery,
    limit = 100,
  } = params;

  return useQuery({
    queryKey: [
      "error-logs",
      severity,
      source,
      entityType,
      entityId,
      resolved,
      startDate?.toISOString(),
      endDate?.toISOString(),
      searchQuery,
      limit,
    ],
    queryFn: async () => {
      let query = supabase
        .from("error_log")
        .select(`
          *,
          user:user_id (
            display_name,
            email
          ),
          resolver:resolved_by (
            display_name
          )
        `)
        .order("occurred_at", { ascending: false })
        .limit(limit);

      if (severity) {
        query = query.eq("severity", severity);
      }

      if (source) {
        query = query.eq("source", source);
      }

      if (entityType) {
        query = query.eq("entity_type", entityType);
      }

      if (entityId) {
        query = query.eq("entity_id", entityId);
      }

      if (resolved !== undefined) {
        query = query.eq("resolved", resolved);
      }

      if (startDate) {
        query = query.gte("occurred_at", startDate.toISOString());
      }

      if (endDate) {
        query = query.lte("occurred_at", endDate.toISOString());
      }

      if (searchQuery) {
        query = query.ilike("message", `%${searchQuery}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ErrorLog[];
    },
  });
}

// ============================================================================
// Hook: useResolveError
// ============================================================================

export function useResolveError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      errorId,
      resolutionNotes,
    }: {
      errorId: string;
      resolutionNotes?: string;
    }) => {
      // Get current user's user_account id
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from("error_log")
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: userData.id,
          resolution_notes: resolutionNotes || null,
        })
        .eq("id", errorId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-logs"] });
      queryClient.invalidateQueries({ queryKey: ["error-log-stats"] });
      toast.success("Error marked as resolved");
    },
    onError: (error: Error) => {
      toast.error(`Failed to resolve error: ${error.message}`);
    },
  });
}

// ============================================================================
// Hook: useUnresolveError
// ============================================================================

export function useUnresolveError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (errorId: string) => {
      const { data, error } = await supabase
        .from("error_log")
        .update({
          resolved: false,
          resolved_at: null,
          resolved_by: null,
          resolution_notes: null,
        })
        .eq("id", errorId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-logs"] });
      queryClient.invalidateQueries({ queryKey: ["error-log-stats"] });
      toast.success("Error marked as unresolved");
    },
    onError: (error: Error) => {
      toast.error(`Failed to unresolve error: ${error.message}`);
    },
  });
}

// ============================================================================
// Hook: useErrorLogStats
// ============================================================================

export function useErrorLogStats() {
  return useQuery({
    queryKey: ["error-log-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("error_log")
        .select("severity, source, resolved");

      if (error) throw error;

      const stats = {
        total: data.length,
        unresolved: data.filter((e) => !e.resolved).length,
        bySeverity: {} as Record<string, number>,
        bySource: {} as Record<string, number>,
        criticalUnresolved: data.filter(
          (e) => e.severity === "critical" && !e.resolved
        ).length,
        errorUnresolved: data.filter(
          (e) => e.severity === "error" && !e.resolved
        ).length,
      };

      data.forEach((e) => {
        stats.bySeverity[e.severity] = (stats.bySeverity[e.severity] || 0) + 1;
        stats.bySource[e.source] = (stats.bySource[e.source] || 0) + 1;
      });

      return stats;
    },
  });
}

// ============================================================================
// Hook: useEntityErrorLogs
// ============================================================================

export function useEntityErrorLogs(entityType: string, entityId: string) {
  return useErrorLogs({ entityType, entityId, limit: 50 });
}
